import * as React from 'react';
import { cn } from '@relay-towing/tokens';
import { ArrowDown } from '@phosphor-icons/react';
import {
  mobilePullArrowRecipe,
  mobilePullRecipe,
  mobileScreenBodyRecipe,
  mobileScreenFooterRecipe,
  mobileScreenHeaderRecipe,
  mobileScreenOverlayRecipe,
  mobileScreenRecipe,
} from '../recipes/mobile.recipe';
import { Spinner } from './Spinner';
import { PULL_THRESHOLD, usePullToRefresh } from './usePullToRefresh';

export interface MobileScreenProps
  extends React.HTMLAttributes<HTMLDivElement> {
  /** The pills along the top: who you are, the call you are on. */
  overlay?: React.ReactNode;
  /** A MobileHeader, for a screen opened from somewhere. Stays put on scroll. */
  header?: React.ReactNode;
  /** The floating tab bar is over this screen; leave room for it. */
  tabBar?: boolean;
  /**
   * What stays along the bottom while the content scrolls: a call's status,
   * on a call with no map.
   */
  footer?: React.ReactNode;
  /**
   * The footer spans the gutter instead of centring: a chat's composer.
   */
  footerFill?: boolean;
  /** A centred column of reading width on a wide screen: a chat. */
  narrow?: boolean;
  /**
   * Pull down from the top to refresh, as a phone's browser does: what the
   * screen shows is fetched again while the indicator spins. A promise
   * keeps it spinning until the refresh is done.
   */
  onRefresh?: () => Promise<unknown> | unknown;
}

/**
 * A phone screen that scrolls: Home, a list, settings. The pills or a header
 * along the top, then the content in the screen's gutter, with the notch and
 * the home indicator kept clear and room left for the tab bar.
 *
 *   <MobileScreen overlay={<PresencePill … />} tabBar>
 *     <SettingsList title="Account">…</SettingsList>
 *   </MobileScreen>
 *
 *   <MobileScreen header={<MobileHeader onBack={back} title="Settings" />}>
 *     …
 *   </MobileScreen>
 *
 * A call with no map is a page too: its header, its details, and its status
 * held along the bottom in `footer`.
 *
 * A screen that is a map is MapSurface instead, which is full-bleed.
 *
 * With `onRefresh`, pulling down from the top refreshes it
 * (usePullToRefresh).
 */
export const MobileScreen = React.forwardRef<HTMLDivElement, MobileScreenProps>(
  function MobileScreen(
    {
      overlay,
      header,
      tabBar = false,
      footer,
      footerFill = false,
      narrow = false,
      onRefresh,
      className,
      children,
      ...props
    },
    ref
  ) {
    const root = React.useRef<HTMLDivElement | null>(null);
    React.useImperativeHandle(ref, () => root.current as HTMLDivElement);
    const pull = usePullToRefresh(root, onRefresh);
    return (
      <div
        ref={root}
        className={cn(mobileScreenRecipe({ narrow }), className)}
        {...props}
      >
        {onRefresh ? (
          <div
            className={mobilePullRecipe({ dragging: pull.dragging })}
            // The pull's own distance, which no class can say.
            style={{ height: pull.distance }}
            role="status"
            aria-live="polite"
          >
            {pull.refreshing ? (
              <Spinner className="mb-3" label="Refreshing" />
            ) : pull.distance > 0 ? (
              <ArrowDown
                aria-hidden
                className={mobilePullArrowRecipe({
                  ready: pull.distance >= PULL_THRESHOLD,
                })}
              />
            ) : null}
          </div>
        ) : null}
        {header ? (
          <div className={mobileScreenHeaderRecipe()}>{header}</div>
        ) : null}
        {overlay ? (
          <div className={mobileScreenOverlayRecipe()}>{overlay}</div>
        ) : null}
        <div
          className={mobileScreenBodyRecipe({
            tabBar,
            bare: !header && !overlay,
          })}
        >
          {children}
        </div>
        {footer ? (
          <div className={mobileScreenFooterRecipe({ fill: footerFill })}>
            {footer}
          </div>
        ) : null}
      </div>
    );
  }
);
